import React, { useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { formatActivityDate } from "./activityFormatting";
import { useRolePermission } from "../../hooks/useRolePermission";
import "./QuotationActivity.scss";

const FIELD_LABELS = {
  aggregateId: "Quotation ID",
  aggregateType: "Record type",
  activityDescription: "Description",
  activityLevel: "Activity level",
  activityReference: "Quotation reference",
  changedColumnsJson: "Changed fields",
  correlationId: "Correlation ID",
  entityId: "Entity ID",
  entityName: "Entity",
  eventCode: "Event code",
  httpMethod: "Request method",
  module: "Module",
  newValuesJson: "New values",
  occurredAtUtc: "Date and time",
  oldValuesJson: "Previous values",
  requestPath: "Request path",
  userIdentityId: "User ID",
  userName: "User",
};

const formatLabel = (key) => FIELD_LABELS[key] ||
  key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
    .replace(/^./, (character) => character.toUpperCase());

const parseJson = (value) => {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const ReadableValue = ({ value, fieldName = "" }) => {
  if (value === null || value === undefined || value === "") return <span>-</span>;
  if (typeof value === "boolean") return <span>{value ? "Yes" : "No"}</span>;

  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}(T|$)/.test(value)) {
    return <span>{formatActivityDate(value)}</span>;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return <span>-</span>;
    return (
      <ul className="activity-detail-list">
        {value.map((item, index) => (
          <li key={`${fieldName}-${index}`}>
            <ReadableValue value={item} fieldName={fieldName} />
          </li>
        ))}
      </ul>
    );
  }

  if (typeof value === "object") {
    const entries = Object.entries(value);
    if (entries.length === 0) return <span>-</span>;
    return (
      <dl className="activity-detail-nested">
        {entries.map(([key, nestedValue]) => (
          <div key={key}>
            <dt>{formatLabel(key)}</dt>
            <dd><ReadableValue value={nestedValue} fieldName={key} /></dd>
          </div>
        ))}
      </dl>
    );
  }

  return <span>{value}</span>;
};

const DetailSection = ({ title, children }) => (
  <section className="quotation-activity-detail-section">
    <h2>{title}</h2>
    <div className="quotation-activity-detail-content">{children}</div>
  </section>
);

const DetailFields = ({ fields }) => (
  <dl className="quotation-activity-detail-fields">
    {fields.map(([label, value]) => (
      <div key={label}>
        <dt>{label}</dt>
        <dd><ReadableValue value={value} fieldName={label} /></dd>
      </div>
    ))}
  </dl>
);

const QuotationActivityDetails = () => {
  const { quotationId, activityId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { menuRights, menuRightsLoading } = useRolePermission("Quotation", "canViewActivities");
  const activity = location.state?.activity;
  const backTarget = location.state?.backTarget || {
    pathname: `/quotation-activities/${encodeURIComponent(quotationId)}`,
  };
  const changedFields = parseJson(activity?.changedColumnsJson);
  const previousValues = parseJson(activity?.oldValuesJson);
  const newValues = parseJson(activity?.newValuesJson);
  const metadata = activity?.activityMetadata;

  useEffect(() => {
    const pathname = `/quotation-activities/${quotationId}/details/${activityId}`;
    window.dispatchEvent(new CustomEvent("pathfinderHeaderMetaChange", {
      detail: {
        pathname,
        title: activity?.activityTitle || "Activity Details",
        backTarget,
      },
    }));

    return () => {
      window.dispatchEvent(new CustomEvent("pathfinderHeaderMetaChange", {
        detail: { pathname, clear: true },
      }));
    };
  }, [activity?.activityTitle, activityId, backTarget, quotationId]);

  if (menuRightsLoading || menuRights?.canViewActivities !== true) return null;

  return (
    <main className="container-fluid quotation-activity-page">
      <div className="quotation-activity-detail-toolbar">
        <button
          type="button"
          className="quotation-activity-back-button"
          onClick={() => navigate(backTarget.pathname, { state: backTarget.state })}
        >
          <i className="fas fa-angle-left" aria-hidden="true" />
          Back to activity
        </button>
      </div>

      {!activity ? (
        <div className="alert alert-warning" role="status">
          Activity details are not available. Return to the activity list and open the details again.
        </div>
      ) : (
        <>
          <header className="quotation-activity-detail-header">
            <span className="quotation-activity-count">
              {activity.changeType || "Activity"}
            </span>
            <h1>{activity.activityTitle || "Quotation activity"}</h1>
            <p>{activity.activityDescription || activity.activity || "No description provided."}</p>
          </header>

          <div className="quotation-activity-detail-grid">
            <DetailSection title="Activity information">
              <DetailFields fields={[
                ["Date and time", formatActivityDate(activity.occurredAtUtc)],
                ["Activity ID", activity.id || activityId || "-"],
                ["User", activity.userName || "-"],
                ["Quotation reference", activity.activityReference || location.state?.quoteReference || "-"],
                ["Activity message", activity.activity || "-"],
                ["Module", activity.module || "-"],
                ["Activity level", activity.activityLevel || "-"],
                ["Change type", activity.changeType || "-"],
                ["Event code", activity.eventCode || "-"],
              ]} />
            </DetailSection>

            <DetailSection title="Related record">
              <DetailFields fields={[
                ["Record type", activity.aggregateType || "-"],
                ["Quotation ID", activity.aggregateId || quotationId || "-"],
                ["Entity", activity.entityName || "-"],
                ["Entity ID", activity.entityId || "-"],
                ["User ID", activity.userIdentityId || "-"],
              ]} />
            </DetailSection>

            {(Array.isArray(changedFields) || changedFields) && (
              <DetailSection title="Changed fields">
                <ReadableValue value={changedFields} fieldName="changedFields" />
              </DetailSection>
            )}

            {previousValues && (
              <DetailSection title="Previous values">
                <ReadableValue value={previousValues} />
              </DetailSection>
            )}

            {newValues && (
              <DetailSection title="New values">
                <ReadableValue value={newValues} />
              </DetailSection>
            )}

            {metadata && Object.keys(metadata).length > 0 && (
              <DetailSection title="Additional information">
                <ReadableValue value={metadata} />
              </DetailSection>
            )}

            {/* <DetailSection title="Request information">
              <DetailFields fields={[
                ["Request method", activity.httpMethod || "-"],
                ["Request path", activity.requestPath || "-"],
                ["Correlation ID", activity.correlationId || "-"],
              ]} />
            </DetailSection> */}
          </div>
        </>
      )}
    </main>
  );
};

export default QuotationActivityDetails;
